import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  getNavGroups,
  crmPath,
  resolveCrmNav,
  type CrmNavGroup,
} from '@/pages/crm/crmNavConfig';

function subNavActive(itemPath: string, section: string) {
  if (itemPath === section) return true;
  if (itemPath === 'customers' && section === 'customers') return true;
  if (itemPath === 'pipeline' && (section === 'pipeline' || section === 'jobs')) return true;
  if (itemPath === 'inbox' && section === 'inbox') return true;
  if (itemPath === 'settings' && section === 'settings') return true;
  return false;
}

/** Desktop / tablet only — mobile uses AppShell hamburger. */
export function CrmNav({ businessId, vertical }: { businessId: string; vertical?: string | null }) {
  const { pathname } = useLocation();
  const { group: activeGroup, section } = resolveCrmNav(pathname, businessId, vertical);
  const navGroups = getNavGroups(vertical);

  return (
    <div className="hidden space-y-2 md:block">
      <nav
        className={cn(
          'grid gap-1 rounded-2xl border border-zinc-800/80 bg-zinc-950/80 p-1 backdrop-blur',
          navGroups.length === 4 ? 'grid-cols-4' : 'grid-cols-6',
        )}
        aria-label="CRM sections"
      >
        {navGroups.map(g => (
          <PrimaryTab
            key={g.id}
            group={g}
            businessId={businessId}
            active={activeGroup.id === g.id}
          />
        ))}
      </nav>

      {activeGroup.items.length > 1 && (
        <nav
          className="rounded-xl border border-zinc-800/60 bg-zinc-900/30 p-1"
          aria-label={`${activeGroup.label} pages`}
        >
          <p className="px-2 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500">
            {activeGroup.label}
          </p>
          <div className="flex flex-wrap gap-1">
            {activeGroup.items.map(item => (
              <NavLink
                key={item.path}
                to={crmPath(businessId, item.path)}
                end={item.path === 'customers' || item.path === 'inbox'}
                className={({ isActive }) =>
                  cn(
                    'inline-flex shrink-0 items-center justify-center rounded-lg px-3.5 py-1.5 text-sm font-medium transition',
                    (isActive || subNavActive(item.path, section))
                      ? 'bg-zinc-800 text-white ring-1 ring-zinc-700'
                      : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

function PrimaryTab({
  group,
  businessId,
  active,
}: {
  group: CrmNavGroup;
  businessId: string;
  active: boolean;
}) {
  const Icon = group.icon;
  const to = crmPath(businessId, group.defaultPath);

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex flex-row items-center gap-2 rounded-xl px-3 py-2.5 text-left transition',
          (isActive || active)
            ? 'bg-gradient-to-b from-cyan-500/20 to-violet-500/15 text-cyan-100 ring-1 ring-cyan-400/30'
            : 'text-zinc-400 hover:bg-zinc-900/60 hover:text-white',
        )
      }
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="text-sm font-medium">{group.label}</span>
    </NavLink>
  );
}
